import { Typography } from 'components/generic/typography';
import styled from 'styled-components';
import type { ClickAndCollectBasketStoreViewModel, OnlineDeliveryOption } from 'autogen/swagger/all';
import { useTranslation } from 'react-i18next';
import {
    OnlineDeliveryHeading,
    OnlineDeliveryNoOption
} from 'autogen/translation-keys/trans-website-basket-deliveryfix';
import HighlightedText from 'components/generic/HighlightedText';
import ProductImage from './ProductImage';
import TruncatedGrid from 'components/TruncatedGrid';
import DefaultToggleButton from 'components/TruncatedGrid/components/DefaultToggleButton';
import { ShowLessProductsText } from 'autogen/translation-keys/trans-website-checkout-delivery';
import Card from 'components/generic/card';
import StatusItem from 'components/StatusItem';
import { useEffect, type Dispatch, type SetStateAction } from 'react';

export type OnlineDeliveryProps = {
    model?: OnlineDeliveryOption;
    selectedDeliveryMethod?: OnlineDeliveryOption | ClickAndCollectBasketStoreViewModel;
    setSelectedDeliveryMethod: Dispatch<
        SetStateAction<OnlineDeliveryOption | ClickAndCollectBasketStoreViewModel | undefined>
    >;
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};
`;

const StyledCard = styled(Card)<{ $selected: boolean }>`
    position: unset;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing12};
    margin-bottom: 0;
    border: 1px solid ${({ $selected, theme }) => ($selected ? theme.ColorBackgroundBrand : theme.ColorBorderDefault)};
    transition: opacity 300ms ease;
    padding: ${({ theme }) => theme.Spacing16};
    cursor: pointer;
`;

function OnlineDelivery(props: OnlineDeliveryProps) {
    const { model, selectedDeliveryMethod, setSelectedDeliveryMethod } = props;
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedDeliveryMethod(model);
    }, []);

    return (
        <Wrapper>
            {model ? (
                <>
                    <Typography.Body as="span" size="L" strong>
                        {t(OnlineDeliveryHeading)}
                    </Typography.Body>
                    <StyledCard
                        onClick={() => setSelectedDeliveryMethod(model)}
                        $selected={selectedDeliveryMethod === model}
                    >
                        <StatusItem color="Yellow" bulletSize={12}>
                            {model.deliveryStatus}
                        </StatusItem>
                        <TruncatedGrid
                            cellWidth={56}
                            cellHeight={72}
                            visibleItems={5}
                            ToggleButtonRenderer={(props) => (
                                <DefaultToggleButton collapseText={t(ShowLessProductsText)} {...props} />
                            )}
                        >
                            {model.productsWithOnlineStockStatus.map((product, i) => (
                                <ProductImage
                                    key={i}
                                    image={product.image.url}
                                    unavailable={!product.isAvailableOnline}
                                />
                            ))}
                        </TruncatedGrid>
                    </StyledCard>
                </>
            ) : (
                <HighlightedText color="ColorSystemNegative1" withBorder={false}>
                    {t(OnlineDeliveryNoOption)}
                </HighlightedText>
            )}
        </Wrapper>
    );
}

export default OnlineDelivery;
